import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, switchMap, catchError, take } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { Action, Store } from '@ngrx/store';

import { State } from '@app/store/state/state';
import { UpsellService } from '@upsell/services/upsell.service';
import * as actions from '@upsell/store/actions/upsell.actions';
import { fetchMessageAction } from '@app/store/actions/global.actions';
import { ToastrTypes } from '@app/entities/toastr';
import { upsellsMapper } from '@upsell/mappers/upsell.mapper';
import { UpsellResponse } from '@shared/flight-itinerary/entities/upsell-flights';
import { InvalidIdType } from '@shared/no-results/entities/results-errors';
import { noResultsSearchFlightsAction } from '@flight-results/store/actions/flights-results.actions';
import { AvoService } from '@avo/services/avo.service';
import { UpsellAbstractEffects } from '@upsell/store/effects/upsell-abstract.effects';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { bookingRootRoute } from '@booking/booking-routing.module';
import { TravelBookingType } from '@reservation/pax/entities/travel-booking-types';
import { getSearchFlowType, getTravelSegment } from '@app/mappers/by-sector-enabled.mapper';

@Injectable()
export class UpsellEffect extends UpsellAbstractEffects {

  constructor(
    private readonly action$: Actions,
    private readonly store: Store<State>,
    private readonly service: UpsellService,
    private readonly router: Router,
    protected translate: TranslocoService,
    protected avoService: AvoService,
    protected amplitudeService: PropertiesAmplitudeService,
  ) {
    super(translate, avoService, amplitudeService);
  }

  fetchUpsellOptions$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchUpsellOptionsAction),
      switchMap(action => this.service.fetchUpsellOptions(action.optionId, action.sessionToken, action.searchId).pipe(
        map(results => {
          return actions.fetchUpsellOptionsSuccessAction({ upsellOptions: { ...results, UpsellOptionId: action.optionId } });
        }),
        catchError((error: HttpErrorResponse) => {
          return this.getUpsellOptionError$(error, SearchBy.FLIGHTS).pipe(
            take(1),
            switchMap(upsellErrorActionToast => {
              const returnActions = [];
              this.navigateOnFlightUpsellTreatment(
                action.isFlightUpsellTreatment,
                action.sessionToken,
                action.searchId,
                action.optionId
              );
              returnActions.push(upsellErrorActionToast);
              returnActions.push(actions.fetchUpsellOptionsErrorAction({ payload: error }));
              if (
                error?.error?.code === InvalidIdType.invalidsearchidoroptionid ||
                error?.error?.code === InvalidIdType.invalidsearchid
              ) {
                returnActions.push(noResultsSearchFlightsAction());
              }
              return returnActions;
            })
          )
        }),
      )),
    ));

  fetchUpsellOptionsBySector$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchUpsellOptionsBySectorAction),
      switchMap(action =>
        this.service.fetchUpsellOptionsBySector(action.optionId, action.sessionToken, action.searchId, action.previousOptions
        ).pipe(
          map((results: UpsellResponse) => {
            if (results.PriceChanged) {
              const toastr = { message: this.textAlertPriceDifferent, type: ToastrTypes.WARNING };
              this.store.dispatch(fetchMessageAction({ toastr }));
            }
            return actions.fetchUpsellOptionsSuccessAction({ upsellOptions: { ...results, UpsellOptionId: action.optionId } });
          }),
          catchError((error: HttpErrorResponse) => {
            return this.getUpsellOptionError$(
              error, SearchBy.FLIGHTS, getSearchFlowType(true), getTravelSegment(true, action.previousOptions)
            ).pipe(
              take(1), switchMap(upsellErrorActionToast => {
                return [
                  upsellErrorActionToast, actions.fetchUpsellOptionsErrorBySectorAction({ payload: error })
                ]
              })
            )
          }),
        )),
    ));

  fetchUpsellOptionsForDesktopAction$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchUpsellOptionsForDesktopAction),
      switchMap(action => this.service.fetchUpsellOptions(action.optionId, action.sessionToken, action.searchId).pipe(
        map(results =>
          actions.fetchUpsellOptionsForDesktopSuccessAction({
            upsellOptions: { ...upsellsMapper(results), UpsellOptionId: action.optionId, AirlineCode: action.airlineCode }
          })
        ),
        catchError((error: HttpErrorResponse) => {
          this.navigateOnFlightUpsellTreatment(
            action.isFlightUpsellTreatment,
            action.sessionToken,
            action.searchId,
            action.optionId
          );
          return this.getUpsellOptionError$(error, SearchBy.FLIGHTS).pipe(
            take(1),
            switchMap(upsellErrorActionToast => {
              return [
                upsellErrorActionToast,
                actions.fetchUpsellOptionsForDesktopErrorAction({ payload: error })
              ]
            })
          )
        }),
      )),
    ));

  fetchUpsellOptionsForPackageUpsellForDesktopAction$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchUpsellOptionsForPackageUpsellForDesktopAction),
      switchMap(action => this.service.fetchPackageUpsellAir(action.upsell).pipe(
        map(results =>
          actions.fetchUpsellOptionsForDesktopSuccessAction({
            upsellOptions: {
              ...upsellsMapper(results),
              UpsellOptionId: Number(action.upsell.optionIdAir), AirlineCode: action.payload.AirlineCode
            }
          })
        ),
        catchError((error: HttpErrorResponse) => {
          return this.getUpsellOptionError$(
            error, SearchBy.PACKAGES
          ).pipe(
            take(1),
            switchMap(upsellErrorActionToast => [
              upsellErrorActionToast,
              actions.fetchUpsellOptionsForDesktopErrorAction({ payload: error })
            ]))
        }),
      )),
    ));

  fetchPackageUpsellOptions$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchPackageUpsellOptionsAction),
      switchMap(action => this.service.fetchPackageUpsellAir(action.payload).pipe(
        map(response => {
          return actions.fetchPackageUpsellOptionsSuccessAction({
            response: {
              ...response,
              UpsellOptionId: Number(action.payload.optionIdAir)
            }
          });
        }),
        catchError((error: HttpErrorResponse) => {
          return this.getUpsellOptionError$(error, SearchBy.PACKAGES).pipe(
            take(1),
            switchMap(upsellErrorActionToast => {
              return [
                upsellErrorActionToast,
                actions.fetchPackageUpsellOptionsErrorAction({ payload: error })
              ]
            })
          )
        }),
      )),
    ));

  get textAlertPriceDifferent(): string {
    return this.translate.translate('BOOKING_MODULE.TEXT_ALERT_PRICE_DIFFERENT');
  }

  private navigateOnFlightUpsellTreatment(
    isFlightUpsellTreatment: boolean,
    sessionToken: string,
    searchId: string,
    optionId: number
  ): void {
    if (isFlightUpsellTreatment) {
      this.router.navigate([
        bookingRootRoute,
        sessionToken,
        searchId,
        optionId,
        'null',
        TravelBookingType.FLIGHTS
      ]);
    }
  }
}
